"use client";

import React, { useState, useMemo, useEffect } from "react";
import type { Variant } from "@/app/utilities/compareTypes";
import {  getVariantByDetailedSlugs } from "@/app/utilities/apis";
import { parseVehicleToken } from "@/app/utilities/slug";
import Image from "next/image";

interface CompareResultProps {
    selectedCars: Variant[];
}

export default function CompareResult({ selectedCars }: CompareResultProps) {
    const [activeTab, setActiveTab] = useState("");
    const [expanded, setExpanded] = useState(false);
    
    // Local client-side copy so we can append a sponsored variant fetched
    // from the `sponsored_variant` cookie when the server didn't include it.
    const [clientCars, setClientCars] = useState<Variant[]>(selectedCars || []);

    // Keep local copy in sync with incoming prop changes
    useEffect(() => setClientCars(selectedCars || []), [selectedCars]);

    // Normalize incoming selectedCars so the rest of the component can
    // rely on `specs: Category[]` where each Category has { category, specs: { name, value }[] }
    const normalizedCars = useMemo(() => {
        return (clientCars || []).map((car) => {
            // If API provides `featureSections`, convert each section into the same
            // { category, specs } shape so feature rows render as their own tab(s)
            // alongside spec categories (features only ever carry a Yes/No value).
            const featureSections = (car as any).featureSections || (car as any).feature_sections || [];
            const featureSpecs = Array.isArray(featureSections) ? featureSections.map((sec: any) => {
                const category = sec.label || sec.key || String(sec._id || "");
                const items = Array.isArray(sec.features) ? sec.features.map((f: any) => {
                    const available = f.available;
                    // If the available field is a number > 1, it's a numeric spec (e.g. no_of_airbags = 6)
                    // stored in featureSections — surface the actual number instead of Yes/No.
                    if (typeof available === 'number' && available > 1) {
                        return {
                            name: f.label || f.key || "",
                            value: String(available),
                        };
                    }
                    const isAvailable = available === true || available === "true" || available === 1;
                    return {
                        name: f.label || f.key || f.name || String(f._id || ""),
                        value: isAvailable ? "Yes" : "No",
                    };
                }) : [];

                return { category, specs: items };
            }) : [];

            // If already has `specs` in the expected shape, just append feature categories
            if (Array.isArray((car as any).specs) && (car as any).specs.length > 0) {
                return { ...car, specs: [...(car as any).specs, ...featureSpecs] } as Variant;
            }

            // If API provides `specSections`, convert them
            const sections = (car as any).specSections || (car as any).spec_sections || [];
            const specSpecs = Array.isArray(sections) ? sections.map((sec: any) => {
                const category = sec.label || sec.key || String(sec._id || "");
                const items = Array.isArray(sec.specs) ? sec.specs.map((s: any) => {
                    // derive display value: prefer explicit value, else valueNormalized + unit, convert booleans
                    let value: any = (s.value !== undefined && s.value !== null) ? s.value : "";
                    if ((value === null || value === "" || value === undefined) && (s.valueNormalized !== undefined && s.valueNormalized !== null)) {
                        value = s.unit ? `${s.valueNormalized} ${s.unit}` : String(s.valueNormalized);
                    }
                    if (typeof value === 'boolean') value = value ? 'Yes' : 'No';
                    value = value === null || value === undefined ? "" : String(value);

                    return {
                        name: s.label || s.key || s.name || String(s._id || ""),
                        value: value.trim(),
                    };
                }) : [];

                return { category, specs: items };
            }) : [];

            const specs = [...specSpecs, ...featureSpecs];
            if (specs.length === 0) return car;

            return { ...car, specs } as Variant;
        });
    }, [clientCars]);

    // When selected/ client-side cars change: if no sponsored variant present,
    // check cookie and fetch it. This runs after a user clicks Compare so the
    // sponsored result is appended immediately instead of only on initial mount.
    useEffect(() => {
        const hasSponsored = (clientCars || []).some((c) => !!(c as any).sponsored);
        if (hasSponsored) return;

        try {
            const match = document.cookie.match(/(?:^|; )sponsored_variant=([^;]+)/);
            const cookieVal = match ? decodeURIComponent(match[1]) : null;
            if (!cookieVal) return;

            const { detailedSlug, variantSlug } = parseVehicleToken(cookieVal);
            if (!detailedSlug || !variantSlug) return;
            
            // fetch variant and append if not already present
            (async () => {
                try {
                    const v = await getVariantByDetailedSlugs(detailedSlug, variantSlug);
                    if (!v) return;
                    // Use functional update to check latest state and avoid duplicates.
                    // If the sponsored variant already exists in selected cars, mark
                    // the existing entry with `sponsored: true` so it will be moved
                    // to the last slot by the padded layout logic. Otherwise append it.
                    setClientCars((prev) => {
                        const cur = prev || [];
                        const existsIdx = cur.findIndex((c) => c && c._id === v._id);
                        if (existsIdx > -1) {
                            return cur.map((c, i) => i === existsIdx ? { ...c, sponsored: true } as Variant : c);
                        }
                        return [...cur, { ...v, sponsored: true } as Variant];
                    });
                } catch (e) {
                    // ignore fetch errors
                }
            })();
        } catch (e) {
            // ignore cookie parsing errors
        }
    }, [clientCars]);

    /* ----------------------------- Helpers ----------------------------- */
    // Helper to detect placeholder entries (created in `paddedCars`).
    const isPlaceholder = (car: Variant) => car._id?.toString().startsWith("__placeholder__");

    // Helper to treat values like 'n/a', 'na' or empty as non-meaningful
    const isMeaningful = (v?: string) => {
        const s = (v ?? "").toString().trim();
        if (!s) return false;
        const lower = s.toLowerCase();
        return lower !== "n/a" && lower !== "na";
    };

    // Small presentational components extracted for clarity
    const HeaderCell: React.FC<{ car: Variant; carIndex: number }> = ({ car, carIndex }) => (
        <th
            key={`h-${car._id}-${carIndex}`}
            scope="col"
            data-col-index={carIndex + 1}
            className="px-4 py-2 text-center align-left min-h-[3rem] max-w-[180px]"
        >
            {isPlaceholder(car) ? (
                <span className="text-xs text-gray-400 italic">&nbsp;</span>
            ) : (
                <>
                    <div className="flex items-center justify-center gap-2">
                        <div className="text-xs font-semibold text-gray-600">{car.brand?.name ?? ""}</div>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{car.model?.name ?? ""} | {car.name}</div>
                </>
            )}
        </th>
    );

    const DataCell: React.FC<{ car: Variant; carIdx: number; value: string; rowValues: string[]; }> = ({ car, carIdx, value, rowValues }) => (
        <td
            key={`c-${car._id}-${carIdx}`}
            data-col-index={carIdx + 1}
            className="px-4 py-2 !text-center align-middle min-h-[3rem] max-w-[180px]"
        >
            {isPlaceholder(car) ? <span className="text-gray-400">&nbsp;</span> : renderValue(value, rowValues)}
        </td>
    );

    /* ----------------------------- Memoized data ----------------------------- */
    const paddedCars = useMemo(() => {
        const maxCols = 4;

        // Fill up to maxCols with provided selected cars (use normalized shape)
        // Deduplicate base by _id while preserving order so the same variant
        // cannot appear more than once in the comparison columns.
        const rawBase = [...normalizedCars];

        const seen = new Set<string | number>();
        const base: Variant[] = [];
        for (const c of rawBase) {
            if (!c) continue;
            const id = c._id ?? `__anon__-${JSON.stringify(c)}`;
            
            seen.add(id);
            base.push(c);
            if (base.length >= maxCols) break;
        }

        // If any car is explicitly marked as sponsored, extract it and
        // place it into the last available empty slot once we build the padded array.
        const sponsorIdx = base.findIndex((c) => !!(c as any).sponsored);
        let sponsor: Variant | null = null;
        if (sponsorIdx > -1) {
            sponsor = base.splice(sponsorIdx, 1)[0];
        }

        // Create placeholders to ensure exactly maxCols items
        const placeholders = Array.from({ length: maxCols }, (_, i) => ({
            _id: `__placeholder__-${i}`,
            brand: { name: "" },
            model: { name: "" },
            name: "",
            specs: [],
        })) as unknown as Variant[];

        const padded = [...base];
        while (padded.length < maxCols) padded.push(placeholders[padded.length]);

        // If we extracted a sponsor, place it into the first empty slot (placeholder).
        // Fall back to the last position if no empty placeholder exists.
        if (sponsor) {
            const firstEmptyIdx = padded.findIndex((c) => isPlaceholder(c));
            if (firstEmptyIdx > -1) {
                padded[firstEmptyIdx] = sponsor;
            } else {
                // No empty slot found — fall back to placing at the last position
                padded[padded.length - 1] = sponsor;
            }
        }

        return padded;
    }, [normalizedCars]);

    

    // We render columns based on `paddedCars` so layout is stable. For
    // placeholder slots we render blank space instead of the "Add a car"
    // label and the dash in cells.

    /* ---------- 3. Full spec map (built using paddedCars order) ---------- */

    /* ---------- 2. Default active tab ---------- */
    // (moved below dynamicTabs declaration)

    /* ---------- 3. Full spec map (built using paddedCars order) ---------- */
    const categoryMap = useMemo(() => {
        // Build a map of category -> specName -> values[] where values align with paddedCars order
        const map = new Map<string, Map<string, string[]>>();

        // First collect all categories and spec names across cars
        const catToNames = new Map<string, Set<string>>();
        paddedCars.forEach((car) => {
            car.specs?.forEach(({ category, specs }) => {
                if (!catToNames.has(category)) catToNames.set(category, new Set());
                const set = catToNames.get(category)!;
                specs.forEach((s) => set.add(s.name));
            });
        });

        // For each category and spec name, build the values array aligned to paddedCars
        catToNames.forEach((names, category) => {
            const specMap = new Map<string, string[]>();
            names.forEach((name) => {
                const values = paddedCars.map((car) => {
                    // First try to find the category as before
                    let cat = car.specs?.find((c) => c.category === category);
                    // If not found, fall back to searching across all categories for the spec name.
                    if (!cat) {
                        cat = car.specs?.find((c) => c.specs.some((s) => s.name === name));
                    }
                    if (!cat) return "";
                    const spec = cat.specs.find((s) => s.name === name);
                    return spec?.value ?? "";
                });
                specMap.set(name, values);
            });
            map.set(category, specMap);
        });

        return map;
    }, [paddedCars]);

    /* ---------- 1. Dynamic tabs (use categoryMap and exclude empty sections) ---------- */
    const dynamicTabs = useMemo(() => {
        const cats: string[] = [];

        categoryMap.forEach((specMap, category) => {
            // include category only if at least one spec has a meaningful value across cars
            let hasMeaningful = false;
            specMap.forEach((vals) => {
                if (vals.some((v) => isMeaningful(v))) hasMeaningful = true;
            });
            if (hasMeaningful) cats.push(category);
        });

        const quickIdx = cats.findIndex((c) => c === "Quick Specs");
        if (quickIdx > -1) {
            cats.splice(quickIdx, 1);
            cats.unshift("Quick Specs");
        } else {
            cats.sort((a, b) => a.localeCompare(b));
        }

        return cats;
    }, [categoryMap]);

    /* ---------- Default active tab ---------- */
    React.useEffect(() => {
        if (dynamicTabs.length && !activeTab) setActiveTab(dynamicTabs[0]);
    }, [dynamicTabs, activeTab]);

    /* ---------- 4. Rows for the current tab ---------- */
    const currentSpecs = useMemo(() => {
        const rows: Array<{ name: string; values: string[] }> = [];

        if (activeTab === "Quick Specs") {
            const quick = ["Fuel Type", "Transmission", "Mileage", "Engine"];
            categoryMap.forEach((specMap) => {
                specMap.forEach((vals, name) => {
                    if (quick.some((k) => name.includes(k))) rows.push({ name, values: vals });
                });
            });
        } else {
            const specMap = categoryMap.get(activeTab);
            specMap?.forEach((vals, name) => rows.push({ name, values: vals }));
        }

        // Filter out rows where all values are empty/NA/placeholders
        const filtered = rows.filter(r => r.values.some(v => isMeaningful(v)));
        return filtered;
    }, [activeTab, categoryMap]);

    const totalRows = currentSpecs.length;
    // Default to collapsed (first 5) for all tabs; expansion toggles to show full list
    const visibleRows = expanded ? currentSpecs : currentSpecs.slice(0, 5);
    const showViewMore = totalRows > 5 && !expanded;




    /* ---------- 6. Render a cell ---------- */
    const renderValue = (raw: string, allValues: string[], specName?: string) => {
        const val = raw?.trim() ?? "";
        const lower = val.toLowerCase();

        // Empty / missing or explicit NA → show 'N/A' text (not a red icon)
        if (!val || lower === "n/a" || lower === "na") {
            return <span className="text-sm text-gray-400 block">N/A</span>;
        }

        // Yes / No icons
        if (lower === "yes") return <div className="flex items-center justify-center w-full">
            <div
                style={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: "#00a63e", 
                    WebkitMaskImage: `url(${process.env.NEXT_PUBLIC_CDN_STATIC_URL}check.svg)`,
                    WebkitMaskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                    WebkitMaskSize: "contain",
                    maskImage: `url(${process.env.NEXT_PUBLIC_CDN_STATIC_URL}check.svg)`,
                    maskRepeat: "no-repeat",
                    maskPosition: "center",
                    maskSize: "contain",
                }}
            />
        </div>;
        if (lower === "no") return <div className="flex items-center justify-center w-full">
                <div
                    style={{
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#e7000b",
                        WebkitMaskImage: `url(${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/cross-icon-new.svg)`,
                        WebkitMaskRepeat: "no-repeat",
                        WebkitMaskPosition: "center",
                        WebkitMaskSize: "contain",
                        maskImage: `url(${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/cross-icon-new.svg)`,
                        maskRepeat: "no-repeat",
                        maskPosition: "center",
                        maskSize: "contain",
                    }}
                />
            </div>;

        // Same value badge — consider only non-empty, meaningful values (ignore placeholders / empty cells)
        const meaningful = (allValues || []).map((v) => (v ?? "").trim()).filter((v) => isMeaningful(v));
        const allSame = meaningful.length >= 2 && meaningful.every((v) => v.toLowerCase() === val.toLowerCase());
        if (allSame) {
            return (
                <span className="inline-flex items-center justify-center px-2.5 py-1 text-xs font-medium text-teal-700 bg-teal-100 rounded-full mx-auto">
                    {val}
                </span>
            );
        }

        // Normal value
        return <span className="text-sm text-gray-700 block">{val}</span>;
    };

    return (
        <div id="compare-grid" className="mx-auto p-4 border border-gray-200 rounded-xl mt-4">
            {/* ---------- Tab Bar (sticky on scroll) ---------- */}
            <div className="sticky top-0 bg-white z-30 border-b border-gray-100 py-2 -mx-4 px-4">
                <div className="overflow-x-auto overflow-y-hidden">
                    <div className="flex items-center gap-1 whitespace-nowrap">
                        {dynamicTabs.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => {
                                    setActiveTab(tab);
                                    setExpanded(false);
                                }}
                                className={`relative px-4 py-2.5 text-sm rounded-t-full font-medium transition-all rounded-t-lg -mb-px cursor-pointer ${activeTab === tab ? "text-teal-600 bg-cyan-100 border-b border-cyan-200 z-10" : "text-gray-600 bg-transparent border-transparent hover:text-gray-900"
                                    }`}
                            >
                                {tab}
                                {activeTab === tab && (
                                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 to-teal-600 rounded-t-full" />
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ---------- Grid Table ---------- */}
            <div className="bg-white overflow-x-auto pr-16 md:pr-0">
                <div className="">
                    {/* Table version: header + rows with sticky first column */}
                    <div className="min-w-max">
                        <table className="min-w-full table-auto border-collapse">
                            <thead className="bg-white">
                                <tr>
                                    <th
                                        scope="col"
                                        className="sticky left-0 top-0 z-30 px-2 py-6 text-xs font-sm text-gray-500 uppercase tracking-wider bg-white w-[120px] md:min-w-[200px] text-left whitespace-normal break-words"
                                    >
                                        Variant
                                    </th>
                                    {paddedCars.map((car, carIndex) => (
                                        <HeaderCell key={`h-${car._id}-${carIndex}`} car={car} carIndex={carIndex} />
                                    ))}
                                </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-100">
                                {visibleRows.map((row, idx) => (
                                    <tr key={row.name} className={`${idx === 0 && activeTab !== "Quick Specs" ? "border-t-2 border-t-gray-200" : ""} hover:bg-gray-50`}>
                                        <th
                                            scope="row"
                                            className="sticky left-0 bg-white z-20 px-2 py-3 text-sm font-medium text-gray-900 align-top w-[120px] md:min-w-[200px] whitespace-normal break-words text-left"
                                        >
                                            {row.name}
                                        </th>

                                        {paddedCars.map((car, carIdx) => {
                                            const cellValue = isPlaceholder(car) ? "" : (row.values[carIdx] ?? "");
                                            return (
                                                <DataCell key={`c-${car._id}-${carIdx}`} car={car} carIdx={carIdx} value={cellValue} rowValues={row.values} />
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* View More / View Less toggle (show when there are more than 5 rows) */}
                    {totalRows > 5 && (
                        <div className="ml-2 pt-2 border-t border-gray-200">
                            {!expanded ? (
                                <button
                                    onClick={() => setExpanded(true)}
                                    className="flex items-center gap-1.5 text-sm font-medium text-blue-600"
                                >
                                    View More                                    
                                        <div
                                            style={{
                                                width: "12px",
                                                height: "12px",
                                                backgroundColor: "#155dfc",
                                                WebkitMask: `url(${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/ChevronRight.svg) no-repeat center / contain`,
                                                mask: `url(${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/ChevronRight.svg) no-repeat center / contain`,
                                                transform: "rotate(90deg)",
                                            }}
                                        />
                                </button>
                            ) : (
                                <button
                                    onClick={() => setExpanded(false)}
                                    className="flex items-center gap-1.5 text-sm font-medium text-blue-600"
                                >
                                    View Less
                                    <div
                                        style={{
                                            width: "12px",
                                            height: "12px",
                                            backgroundColor: "#155dfc",
                                            WebkitMask: `url(${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/ChevronRight.svg) no-repeat center / contain`,
                                            mask: `url(${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/ChevronRight.svg) no-repeat center / contain`,
                                            transform: "rotate(270deg)",
                                        }}
                                    />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}